/**
 * `ApiError` — the ONLY error type `@reflecto/client-core/api` throws (R3-b).
 *
 * It carries no exit code, no i18n key and no host vocabulary, because the same client serves
 * a CLI that exits with a number, a browser that renders a toast, and an Electron main process
 * that does neither. Before this existed, `apps/cli/src/transport/api.ts` threw `CliError` —
 * process-exit vocabulary, out of what is structurally a library — which is exactly what made
 * the client un-shareable. Hosts map at their own boundary (the CLI's is `toCliError`).
 *
 * ⚠️ NAME COLLISION, deliberate and worth knowing about. `@reflecto/core` also exports an
 * `ApiError` — but it is an `interface` describing the JSON error BODY a route returns
 * (`{ error, message? }`), not a throwable. The two are unrelated: this one is the thrown
 * class, that one is wire shape. Modules needing both import the wire type aliased
 * (`import type { ApiError as ApiErrorBody }`), which is what `client.ts` does. Renaming
 * either would be the tidier-looking fix and the wrong one — `ApiError` is the correct name
 * for a thrown API error, and the wire interface is load-bearing across the repo.
 */

export class ApiError extends Error {
  /**
   * HTTP status, or `null` when the request never reached the server at all.
   *
   * The null case is not a detail: "the server said no" and "we could not ask" have different
   * recoveries — re-authenticate versus retry — and collapsing them is how a transient network
   * blip gets reported to a user as an auth failure.
   */
  readonly status: number | null;

  /**
   * The server's own error code (`unauthorized`, `not_paired_with_target`, `no_paired_device`,
   * …), `http_<status>` when a non-2xx carried no JSON body, or one of two client-side codes:
   * `network_error` (the request never left) and `token_unavailable` (the host's token source
   * threw). Those two are the shipped strings — an earlier draft of this comment said
   * `"network"`, which no code path has ever produced.
   */
  readonly code: string;

  /**
   * The `message` the SERVER sent, or `null` when it sent none (no body, not JSON, or a body
   * without a usable `message`).
   *
   * Kept separate from `message` because `message` is never empty — it falls back to
   * `HTTP <status>` so an error is always readable. That fallback destroys the distinction
   * between "the server explained itself" and "we made something up", and a host that needs
   * to substitute its own wording cannot recover it: string-matching `message` against
   * `` `HTTP ${status}` `` would be a guess that silently breaks the day the fallback changes.
   *
   * `apps/web` needs exactly this. Its client throws
   * `` body.message || `DELETE /messages/${id} failed (${status})` ``, so a wrapper over this
   * module has to know which half applied. Same shape of lossiness the 403/404 code
   * normalisation had before PR B-i — a synthesised value overwriting a real one.
   */
  readonly serverMessage: string | null;

  /**
   * For `network_error` only: the error `fetch` actually rejected with, preserved as the
   * standard `Error.cause` so a host can re-throw the ORIGINAL rather than a paraphrase.
   *
   * Why this exists, concretely: `apps/extension`'s dismiss-rollback path logs the network
   * failure it caught, and its suite pins that the logged text is the runtime's own message.
   * Wrapping the rejection here turned `"Network failure"` into
   * `"Failed to reach <origin>: Network failure"` — readable, but not what the caller had
   * before, and a wrapper over this module could not get the original back from a string.
   * `apps/web`'s `toWebError` had the same gap; its suite simply did not pin it.
   *
   * `message` still carries the readable paraphrase for hosts that want it. `cause` is the
   * thing itself.
   *
   * Declared as an own field rather than through `ErrorOptions`, because not every consumer
   * compiles against an ES2022 lib (`apps/web` does not) and a field is the same thing on
   * every target.
   */
  readonly cause: unknown;

  constructor(
    code: string,
    message: string,
    status: number | null,
    serverMessage?: string | null,
    cause?: unknown
  ) {
    super(message);
    this.cause = cause;
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.serverMessage = serverMessage ?? null;
  }
}

/** Narrowing helper — `instanceof` across a package boundary is fine here (single copy). */
export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}
